import { useEffect, useRef, useState, type JSX } from 'react';
import type { PropertyDescriptor, SelectOption } from '../../../core/definitions';
import { pickFiles } from '../../actions/files';
import { Icon } from '../../components/Icon';
import { NumberField } from '../../components/NumberField';
import { Slider } from '../../components/Slider';
import { useRuntime, useStudio } from '../../context';
import { AssetImportError } from '../../../persist/api';
import { errorMessage, safely } from '../../safe';
import { assetLabel, knownAssetName, rememberAssetName } from './assetNames';
import { coerceNumber, coerceSelect, guestKey, parseCssColor, parseHexColor, toHexColor } from './coerce';

type Of<T extends PropertyDescriptor['type']> = Extract<PropertyDescriptor, { type: T }>;

export interface FieldProps<T extends PropertyDescriptor['type'], V> {
  desc: Of<T>;
  value: V;
  id: string;
  onChange(value: unknown): void;
  /** A discrete edit finished (slider released, field blurred). */
  onCommit(): void;
  /** Reports a value the field refuses to apply (null once it is acceptable again), so the dialog can say so. */
  onInvalid?(problem: string | null): void;
}

function runValidate(validate: ((value: string) => string | null) | undefined, value: string): string | null {
  if (!validate) return null;
  try {
    return validate(value);
  } catch (error) {
    return errorMessage(error);
  }
}

/**
 * Free text. With applyOn 'commit' the source sees the text only when the
 * field is left or Enter is pressed (Ctrl/Cmd+Enter in a multi-line box); a
 * value `validate` rejects is shown with its problem and never applied.
 */
export function TextField({ desc, value, id, onChange, onCommit, onInvalid }: FieldProps<'text', string>): JSX.Element {
  const [draft, setDraft] = useState<string | null>(null);
  const commitMode = desc.applyOn === 'commit';
  const text = draft ?? value;
  const problem = runValidate(desc.validate, text);
  const problemId = `${id}-problem`;

  useEffect(() => onInvalid?.(problem), [onInvalid, problem]);
  // An outside change (Defaults, undo) replaces a draft that was not being edited into it.
  useEffect(() => setDraft((current) => (current === value ? null : current)), [value]);

  const edit = (next: string): void => {
    if (commitMode || runValidate(desc.validate, next) !== null) {
      setDraft(next);
      return;
    }
    setDraft(null);
    onChange(next);
  };
  const finish = (): void => {
    if (draft !== null && runValidate(desc.validate, draft) === null) {
      if (draft !== value) onChange(draft);
      setDraft(null);
    }
    onCommit();
  };

  const common = {
    id,
    value: text,
    placeholder: desc.placeholder,
    'aria-invalid': problem !== null || undefined,
    'aria-describedby': problem ? problemId : undefined,
    onBlur: finish,
  };
  const field = desc.multiline ? (
    <textarea
      {...common}
      className={problem ? 'ui-textarea invalid' : 'ui-textarea'}
      onChange={(e) => edit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) finish();
      }}
    />
  ) : (
    <input
      {...common}
      className={problem ? 'ui-input invalid' : 'ui-input'}
      type={desc.inputType === 'password' ? 'password' : desc.inputType === 'url' ? 'url' : 'text'}
      inputMode={desc.inputType === 'url' ? 'url' : undefined}
      autoComplete={desc.inputType === 'password' ? 'off' : undefined}
      spellCheck={desc.inputType === 'url' || desc.inputType === 'password' ? false : undefined}
      onChange={(e) => edit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') finish();
      }}
    />
  );
  return (
    <div className="ui-col" style={{ gap: 4 }}>
      {field}
      {problem ? (
        <span id={problemId} className="ui-error-text" role="alert">
          {problem}
        </span>
      ) : commitMode && draft !== null && draft !== value ? (
        <span className="ui-dim">Applies when you press Enter or leave the field.</span>
      ) : null}
    </div>
  );
}

export function NumberProp({ desc, value, id, onChange, onCommit }: FieldProps<'number', number>): JSX.Element {
  const commitMode = desc.applyOn === 'commit';
  // While a commit-mode slider is dragged only the bar moves; the value is applied on release.
  const [sliding, setSliding] = useState<number | null>(null);
  // Read on release: a keyboard step changes and commits in the same event, before the state above updates.
  const slid = useRef<number | null>(null);
  const field = (
    <NumberField
      id={id}
      label={desc.label}
      value={value}
      min={desc.min}
      max={desc.max}
      step={desc.step ?? 1}
      unit={desc.unit}
      onChange={(next) => onChange(coerceNumber(next, { min: desc.min, max: desc.max }))}
      onCommit={onCommit}
      applyOn={desc.applyOn}
    />
  );
  if (!desc.slider || desc.min === undefined || desc.max === undefined) return field;
  return (
    <div className="ui-row">
      <Slider
        label={desc.label}
        value={sliding ?? value}
        min={desc.min}
        max={desc.max}
        step={desc.step ?? 0}
        valueText={`${sliding ?? value}${desc.unit ? ` ${desc.unit}` : ''}`}
        onChange={(next) => {
          const coerced = coerceNumber(next, desc);
          if (!commitMode) {
            onChange(coerced);
            return;
          }
          slid.current = coerced;
          setSliding(coerced);
        }}
        onCommit={() => {
          if (commitMode && slid.current !== null && slid.current !== value) onChange(slid.current);
          slid.current = null;
          setSliding(null);
          onCommit();
        }}
      />
      {field}
    </div>
  );
}

export function BooleanField({ desc, value, id, onChange, onCommit }: FieldProps<'boolean', boolean>): JSX.Element {
  return (
    <span className="ui-check">
      <input
        id={id}
        type="checkbox"
        checked={value}
        aria-label={desc.label}
        onChange={(e) => {
          onChange(e.target.checked);
          onCommit();
        }}
      />
    </span>
  );
}

export function SelectField({ desc, value, id, onChange, onCommit }: FieldProps<'select', string | number>): JSX.Element {
  const source = desc.options;
  const isStatic = Array.isArray(source);
  const [fetched, setFetched] = useState<{ options: SelectOption[]; error: string | null; loading: boolean }>({
    options: [],
    error: null,
    loading: !isStatic,
  });
  const [reload, setReload] = useState(0);
  // Descriptors are rebuilt on every settings change, so the loader's identity is not a usable dependency.
  const loader = useRef(source);
  loader.current = source;

  useEffect(() => {
    const load = loader.current;
    if (Array.isArray(load)) return;
    let cancelled = false;
    setFetched((prev) => ({ ...prev, loading: true }));
    Promise.resolve()
      .then(() => load())
      .then(
        (options) => !cancelled && setFetched({ options, error: null, loading: false }),
        (error: unknown) => !cancelled && setFetched({ options: [], error: errorMessage(error), loading: false }),
      );
    return () => {
      cancelled = true;
    };
  }, [desc.key, isStatic, reload]);

  const all = Array.isArray(source) ? source : fetched.options;
  const loading = !isStatic && fetched.loading;
  const [filter, setFilter] = useState('');
  const needle = filter.trim().toLowerCase();
  // The current choice stays listed so the select never shows something other than the stored value.
  const options = needle
    ? all.filter((o) => String(o.value) === String(value) || o.label.toLowerCase().includes(needle) || String(o.value).toLowerCase().includes(needle))
    : all;
  const known = options.some((o) => String(o.value) === String(value));
  return (
    <>
      {desc.searchable ? (
        <input
          className="ui-input"
          type="search"
          placeholder="Filter…"
          aria-label={`Filter ${desc.label}`}
          aria-controls={id}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ marginBottom: 4 }}
        />
      ) : null}
      <div className="ui-row">
        <select
          id={id}
          className="ui-select ui-grow"
          value={String(value)}
          aria-busy={loading}
          onChange={(e) => {
            onChange(coerceSelect(e.target.value, options));
            onCommit();
          }}
        >
          {!known ? <option value={String(value)}>{loading ? 'Loading…' : value === '' ? 'Select…' : String(value)}</option> : null}
          {options.map((option) => (
            <option key={String(option.value)} value={String(option.value)}>
              {option.label}
            </option>
          ))}
        </select>
        {!isStatic ? (
          <button type="button" className="ui-icon-btn" aria-label={`Reload ${desc.label} options`} title="Reload" onClick={() => setReload((n) => n + 1)}>
            <Icon name="refresh" />
          </button>
        ) : null}
      </div>
      {!isStatic && fetched.error ? <span className="ui-field-help ui-error-text">Could not load the options: {fetched.error}</span> : null}
      {needle && !options.some((o) => String(o.value) !== String(value)) ? <span className="ui-dim">No other matches.</span> : null}
    </>
  );
}

/**
 * Colour: the native picker (which has no alpha, so the stored alpha is kept),
 * a text box that also takes rgb()/rgba()/hsl()/hsla() and stores hex, an
 * opacity slider when the descriptor allows alpha, and optional swatches.
 */
export function ColorField({ desc, value, id, onChange, onCommit }: FieldProps<'color', string>): JSX.Element {
  const alpha = desc.alpha === true;
  const color = parseHexColor(value) ?? { r: 0, g: 0, b: 0, a: 1 };
  const [draft, setDraft] = useState<string | null>(null);
  const invalid = draft !== null && parseCssColor(draft) === null;
  const store = (next: { r: number; g: number; b: number; a: number }): void => onChange(toHexColor(next, alpha));
  return (
    <div className="ui-col" style={{ gap: 6 }}>
      <div className="ui-row">
        <input
          id={id}
          type="color"
          aria-label={`${desc.label} colour`}
          value={toHexColor(color, false)}
          onChange={(e) => {
            const picked = parseHexColor(e.target.value);
            if (picked) store({ ...picked, a: color.a });
          }}
          onBlur={onCommit}
        />
        <input
          className={invalid ? 'ui-input invalid' : 'ui-input'}
          style={{ width: 150 }}
          aria-label={`${desc.label} hex value`}
          aria-invalid={invalid || undefined}
          title="Hex, rgb(), rgba(), hsl() or hsla()"
          spellCheck={false}
          value={draft ?? value}
          onChange={(e) => {
            setDraft(e.target.value);
            const parsed = parseCssColor(e.target.value);
            if (parsed) store(alpha ? parsed : { ...parsed, a: 1 });
          }}
          onBlur={() => {
            setDraft(null);
            onCommit();
          }}
        />
        {alpha ? (
          <Slider
            label={`${desc.label} opacity`}
            value={Math.round(color.a * 100)}
            min={0}
            max={100}
            step={1}
            valueText={`${Math.round(color.a * 100)}% opaque`}
            onChange={(next) => store({ ...color, a: next / 100 })}
            onCommit={onCommit}
          />
        ) : null}
      </div>
      {desc.presets && desc.presets.length > 0 ? (
        <div className="property-swatches" role="group" aria-label={`${desc.label} presets`}>
          {desc.presets.map((preset, index) => {
            const parsed = parseCssColor(preset);
            if (!parsed) return null;
            const hex = toHexColor(parsed, alpha);
            return (
              <button
                key={`${preset}:${index}`}
                type="button"
                className={hex.toLowerCase() === value.toLowerCase() ? 'property-swatch selected' : 'property-swatch'}
                style={{ background: toHexColor(parsed, true) }}
                aria-label={`Use ${hex}`}
                aria-pressed={hex.toLowerCase() === value.toLowerCase()}
                title={hex}
                onClick={() => {
                  onChange(hex);
                  onCommit();
                }}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/** An asset's file name; assets imported in an earlier session are looked up in the asset store. */
const ADDRESS = /^(https?|blob|data):/i;

/** An asset's file name, or the address itself for entries that point at a URL instead of an imported file. */
export function AssetName({ assetId }: { assetId: string }): JSX.Element {
  if (ADDRESS.test(assetId)) {
    return (
      <span className="ui-grow ui-ellipsis ui-selectable" title={assetId}>
        {assetId}
      </span>
    );
  }
  return <ImportedAssetName assetId={assetId} />;
}

function ImportedAssetName({ assetId }: { assetId: string }): JSX.Element {
  const studio = useStudio();
  const [name, setName] = useState(() => knownAssetName(assetId));
  useEffect(() => {
    const known = knownAssetName(assetId);
    setName(known);
    if (known !== null) return;
    let cancelled = false;
    studio.getAssetMeta(assetId).then(
      (meta) => {
        if (!meta) return;
        rememberAssetName(meta.id, meta.name);
        if (!cancelled) setName(meta.name);
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [studio, assetId]);
  return (
    <span className="ui-grow ui-ellipsis" title={name ?? assetId}>
      {name ?? assetLabel(assetId)}
    </span>
  );
}

const IMPORT_PROBLEMS: Record<AssetImportError['code'], string> = {
  'too-large': 'is too large to import',
  unsupported: 'is not a file type this property accepts',
  'storage-full': 'could not be stored: the browser storage is full',
};

/** Why one file could not be imported, in words the operator can act on. */
export function importProblem(fileName: string, error: unknown): string {
  if (error instanceof AssetImportError) return `"${fileName}" ${IMPORT_PROBLEMS[error.code]}. ${error.message}`;
  return `"${fileName}" could not be imported: ${errorMessage(error)}`;
}

export interface AssetImport {
  busy: boolean;
  /** Stops the import in progress; files already imported are kept. */
  cancel(): void;
  /** 0..1 over the whole batch while importing, null otherwise. */
  progress: number | null;
  problems: string[];
  /** Opens the file picker and imports what is chosen; resolves with the ids that imported. */
  browse(): Promise<string[]>;
}

/** File picking and import with progress; a file that fails is reported and the others still import. */
export function useAssetImport(accept: string, multiple: boolean): AssetImport {
  const studio = useStudio();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const controller = useRef<AbortController | null>(null);
  useEffect(() => () => controller.current?.abort(), []);
  const browse = async (): Promise<string[]> => {
    const files = await pickFiles(accept, multiple);
    if (files.length === 0) return [];
    const abort = new AbortController();
    controller.current = abort;
    setBusy(true);
    setProblems([]);
    setProgress(0);
    const imported: string[] = [];
    const failed: string[] = [];
    try {
      for (const [index, file] of files.entries()) {
        if (abort.signal.aborted) break;
        try {
          // The store applies its own size limit (MAX_IMPORT_BYTES) when none is given.
          const meta = await studio.importAsset(file, file.name, {
            signal: abort.signal,
            onProgress: (fraction) => setProgress((index + Math.min(1, Math.max(0, fraction))) / files.length),
          });
          rememberAssetName(meta.id, meta.name);
          imported.push(meta.id);
        } catch (error) {
          if (abort.signal.aborted) break;
          failed.push(importProblem(file.name, error));
        }
        setProgress((index + 1) / files.length);
      }
    } finally {
      if (controller.current === abort) controller.current = null;
      setBusy(false);
      setProgress(null);
      setProblems(failed);
    }
    return imported;
  };
  return { busy, progress, problems, browse, cancel: () => controller.current?.abort() };
}

export function ImportStatus({ state }: { state: AssetImport }): JSX.Element | null {
  if (state.progress !== null) {
    return (
      <span className="ui-row ui-dim" role="status">
        <progress max={1} value={state.progress} aria-label="Import progress" /> {Math.round(state.progress * 100)}%
        <button type="button" className="ui-btn" onClick={state.cancel}>
          Cancel import
        </button>
      </span>
    );
  }
  if (state.problems.length === 0) return null;
  return (
    <div role="alert" className="ui-error-text ui-selectable">
      {state.problems.map((problem) => (
        <div key={problem}>{problem}</div>
      ))}
    </div>
  );
}

export function AssetField({ desc, value, id, onChange, onCommit }: FieldProps<'asset', string | string[]>): JSX.Element {
  const studio = useStudio();
  const importer = useAssetImport(desc.accept, desc.multiple === true);
  const ids = Array.isArray(value) ? value : value ? [value] : [];

  const browse = (): void =>
    void safely(studio, async () => {
      const imported = await importer.browse();
      if (imported.length === 0) return;
      onChange(desc.multiple ? [...ids, ...imported] : imported[0]);
      onCommit();
    }, 'Import failed');

  // The same file may be listed twice; rows are told apart by position, and Remove takes out just one.
  const remove = (index: number): void => {
    onChange(desc.multiple ? ids.filter((_, i) => i !== index) : '');
    onCommit();
  };

  return (
    <div className="ui-col">
      {ids.length === 0 ? <span className="ui-dim">No file selected</span> : null}
      {ids.map((assetId, index) => (
        <div key={`${index}:${assetId}`} className="ui-row">
          <AssetName assetId={assetId} />
          <button type="button" className="ui-btn" onClick={() => remove(index)}>
            Remove
          </button>
        </div>
      ))}
      <div>
        <button id={id} type="button" className="ui-btn" disabled={importer.busy} aria-busy={importer.busy} onClick={browse}>
          {importer.busy ? 'Importing…' : desc.multiple ? 'Add files…' : ids.length > 0 ? 'Replace…' : 'Browse…'}
        </button>
      </div>
      <ImportStatus state={importer} />
    </div>
  );
}

const WEB_FONTS = [
  'system-ui', 'sans-serif', 'serif', 'monospace', 'Arial', 'Helvetica', 'Verdana', 'Tahoma', 'Trebuchet MS', 'Georgia',
  'Times New Roman', 'Courier New', 'Impact', 'Segoe UI', 'Roboto', 'Inter', 'Noto Sans',
];

interface LocalFont {
  family: string;
}

export function FontField({ value, id, onChange, onCommit }: FieldProps<'font', string>): JSX.Element {
  const studio = useStudio();
  const [fonts, setFonts] = useState<string[]>(WEB_FONTS);
  const query = (window as unknown as { queryLocalFonts?: () => Promise<LocalFont[]> }).queryLocalFonts;
  const listId = `${id}-fonts`;

  const loadInstalled = (): void => {
    void safely(studio, async () => {
      const local = await query!.call(window);
      setFonts([...new Set([...local.map((f) => f.family), ...WEB_FONTS])].sort((a, b) => a.localeCompare(b)));
    }, 'Could not read installed fonts');
  };

  return (
    <div className="ui-row">
      <input
        id={id}
        className="ui-input ui-grow"
        list={listId}
        value={value}
        style={{ fontFamily: value || undefined }}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onCommit}
      />
      <datalist id={listId}>
        {fonts.map((font) => (
          <option key={font} value={font} />
        ))}
      </datalist>
      {typeof query === 'function' ? (
        <button type="button" className="ui-btn" onClick={loadInstalled}>
          List installed
        </button>
      ) : null}
    </div>
  );
}

/**
 * Guest picker. The stored value is the guest's `participantId` when the
 * guest has one (it survives reconnects and new broadcasts), else its
 * connection `guestId`; a stored value matches a guest by either.
 */
export function GuestField({ value, id, onChange, onCommit }: FieldProps<'guest', string>): JSX.Element {
  const guests = useRuntime((r) => r.guests);
  const current = guests.find((g) => value !== '' && (g.participantId === value || g.guestId === value));
  const selected = current ? guestKey(current) : value;
  return (
    <>
      <select
        id={id}
        className="ui-select"
        value={selected}
        onChange={(e) => {
          onChange(e.target.value);
          onCommit();
        }}
      >
        <option value="">{guests.length === 0 ? 'No guests connected' : 'Select a guest…'}</option>
        {value !== '' && !current ? <option value={value}>Disconnected guest ({value.slice(0, 8)})</option> : null}
        {guests.map((guest) => (
          <option key={guest.guestId} value={guestKey(guest)}>
            {guest.name || guest.guestId.slice(0, 8)} — {guest.connectionState}
          </option>
        ))}
      </select>
      {guests.length === 0 ? <span className="ui-field-help">Start streaming and share the guest link to bring someone in.</span> : null}
    </>
  );
}
